import React, { useCallback, useMemo, useEffect, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  Panel,
  Connection,
  Edge,
  ConnectionMode,
  useReactFlow,
  Node,
  NodeChange,
  EdgeChange,
  SelectionMode,
  MarkerType,
  PanOnScrollMode,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import { AgentNode } from '../nodes/AgentNode';
import { TaskNode } from '../nodes/TaskNode';
import { ToolNode } from '../nodes/ToolNode';
import { FlowNode } from '../nodes/FlowNode';
import { useFlow } from '../../lib/hooks/useFlow';
import { TestResultsModal } from '../TestResultsModal';
import { validateFlow } from '../../lib/validateFlow';
import { CanvasControls } from './CanvasControls';
import { CanvasToolbar } from './CanvasToolbar';
import { CanvasValidation } from './CanvasValidation';
import { CanvasEmptyState } from './CanvasEmptyState';
import { flowTheme } from '../../lib/theme/flowTheme';
import { useEditorStore } from '../../lib/editorStore';

const nodeTypes = {
  agent: AgentNode,
  task: TaskNode,
  tool: ToolNode,
  flow: FlowNode,
};

interface CanvasProps {
  testResults: any[] | null;
  onCloseTestResults: () => void;
  onNodePropertyChange: (nodeId: string, property: string, value: any) => void;
  onNodeLabelChange: (nodeId: string, label: string) => void;
  onTestFlow: () => void;
  onGenerateCode: () => void;
  onSaveFlow: () => void;
  onClearCanvas: () => void;
  isSaving: boolean;
  lastSavedAt: Date | null;
  showVersionHistory: boolean;
  onToggleVersionHistory: () => void;
}

export function Canvas({
  testResults,
  onCloseTestResults,
  onNodePropertyChange,
  onNodeLabelChange,
  onTestFlow,
  onGenerateCode,
  onSaveFlow,
  onClearCanvas,
  isSaving,
  lastSavedAt,
  showVersionHistory,
  onToggleVersionHistory,
}: CanvasProps) {
  const {
    nodes,
    edges,
    selectedNodeId,
    isDragging,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onDrop,
    onDragStart,
    onDragEnd,
    updateNode,
    setNodes,
    setEdges,
    addNode,
    setSelectedNode,
  } = useFlow();

  const [validationErrors, setValidationErrors] = useState<any[]>([]);
  const { getViewport, screenToFlowPosition } = useReactFlow();
  const clearCanvas = useEditorStore(state => state.clearCanvas);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + S to save
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        onSaveFlow();
      }
      // Delete/Backspace to remove selected nodes
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        selectedNodeId &&
        !(event.target as Element)?.matches('input, textarea')
      ) {
        const changes: NodeChange[] = [
          {
            type: 'remove',
            id: selectedNodeId,
          },
        ];
        onNodesChange(changes);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId, onNodesChange, onSaveFlow]);

  // Validate flow on changes
  useEffect(() => {
    const errors = validateFlow(nodes, edges);
    setValidationErrors(errors);
  }, [nodes, edges]);

  // Enhanced node selection handling
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.stopPropagation();
      // Only allow editing if the node isn't already being edited
      if (!node.data?.isEditing) {
        updateNode(node.id, { data: { ...node.data, selected: true } });
      }
    },
    [updateNode]
  );

  // Handle canvas click to deselect
  const onPaneClick = useCallback(() => {
    if (selectedNodeId) {
      updateNode(selectedNodeId, { data: { selected: false } });
    }
  }, [selectedNodeId, updateNode]);

  // Handle template application
  const handleApplyTemplate = useCallback((template: any) => {
    // Get current viewport
    const viewport = getViewport();
    
    // Get ReactFlow container
    const reactFlowContainer = document.querySelector('.react-flow');
    if (!reactFlowContainer) return;

    // Get container dimensions
    const rect = reactFlowContainer.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Convert screen coordinates to flow coordinates
    const position = screenToFlowPosition({
      x: centerX,
      y: centerY,
    });

    // Adjust for zoom level
    const currentViewport = getViewport();
    const zoom = currentViewport.zoom || 1;
    position.x = position.x / zoom;
    position.y = position.y / zoom;

    const newNode = {
      ...template,
      id: `${template.type}-${nodes.length + 1}`,
      position,
      data: {
        ...template.data,
        onLabelChange: (newLabel: string) =>
          onNodeLabelChange(`${template.type}-${nodes.length + 1}`, newLabel),
        onPropertyChange: (property: string, value: any) =>
          onNodePropertyChange(
            `${template.type}-${nodes.length + 1}`,
            property,
            value
          ),
        selected: false,
      },
      dragHandle: '.node-drag-handle',
    };

    // Add node directly instead of simulating drop
    addNode(newNode);
    setSelectedNode(newNode.id);
  }, [nodes.length, onNodeLabelChange, onNodePropertyChange, getViewport, screenToFlowPosition, addNode, setSelectedNode]);

  // Handle clear canvas
  const handleClearCanvas = useCallback(() => {
    clearCanvas();
    onClearCanvas();
  }, [clearCanvas, onClearCanvas]);

  // Memoized edge options
  const defaultEdgeOptions = useMemo(
    () => ({
      ...flowTheme.edges.default,
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: flowTheme.edges.default.style.stroke,
      },
    }),
    []
  );

  return (
    <div className="flex-1 h-full bg-gray-900 relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }}
        onDrop={onDrop}
        nodeTypes={nodeTypes}
        nodesDraggable={!isDragging}
        nodesConnectable={!isDragging}
        onNodeDragStart={onDragStart}
        onNodeDragStop={onDragEnd}
        elementsSelectable={true}
        selectNodesOnDrag={true}
        multiSelectionKeyCode="Shift"
        selectionMode={SelectionMode.Partial}
        selectionOnDrag={true}
        panOnScroll={true}
        panOnScrollMode={PanOnScrollMode.Free}
        panOnDrag={true}
        zoomOnScroll={true}
        minZoom={0.1}
        maxZoom={4}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        className="bg-gray-900"
        snapToGrid={true}
        snapGrid={[16, 16]}
        connectionMode={ConnectionMode.Loose}
      >
        <Background color="#374151" gap={16} size={1} />

        {/* Toolbar */}
        <Panel position="top-center" className="w-full">
          <CanvasToolbar
            onTestFlow={onTestFlow}
            onGenerateCode={onGenerateCode}
            onSaveFlow={onSaveFlow}
            onClearCanvas={() => onClearCanvas()}
            onToggleVersionHistory={onToggleVersionHistory}
            isSaving={isSaving}
            lastSavedAt={lastSavedAt}
            showVersionHistory={showVersionHistory}
            onApplyTemplate={handleApplyTemplate}
          />
        </Panel>

        {/* Controls */}
        <Panel position="bottom-left" className="m-4">
          <CanvasControls />
        </Panel>

        {/* Validation */}
        <Panel position="bottom-right" className="m-4">
          <CanvasValidation errors={validationErrors} />
        </Panel>
      </ReactFlow>

      {/* Empty State */}
      {nodes.length === 0 && <CanvasEmptyState />}

      {/* Modals */}
      {testResults && (
        <TestResultsModal results={testResults} onClose={onCloseTestResults} />
      )}
    </div>
  );
}
